import { Heart, Plus, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { componentCategories } from "@/data/componentCategories";

export interface Component {
  id: string;
  name: string;
  mainCategory: string;
  subCategory: string;
  image: string;
  colors: string[];
  finish: string;
  isFavorite?: boolean;
}

interface ComponentCardProps {
  component: Component;
  onFavorite: (id: string) => void;
  onAddToProject: (id: string) => void;
  onClick?: (component: Component) => void;
}

export function ComponentCard({
  component,
  onFavorite,
  onAddToProject,
  onClick,
}: ComponentCardProps) {
  // Get readable category names
  const mainCat = componentCategories.find((c) => c.id === component.mainCategory);
  const subCat = mainCat?.subCategories.find((s) => s.id === component.subCategory);

  return (
    <div
      className="group relative cursor-pointer overflow-hidden bg-card transition-all duration-500"
      onClick={() => onClick?.(component)}
    >
      {/* Image — 4:3 ratio, clean */}
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={component.image}
          alt={component.name}
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        {/* Subtle vignette */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

        {/* Favorite */}
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "absolute right-2.5 top-2.5 h-7 w-7 rounded-full transition-all duration-300",
            component.isFavorite
              ? "bg-foreground/90 text-background"
              : "bg-background/60 text-muted-foreground opacity-0 backdrop-blur-sm group-hover:opacity-100 hover:bg-background/80"
          )}
          onClick={(e) => {
            e.stopPropagation();
            onFavorite(component.id);
          }}
        >
          <Heart className={cn("h-3.5 w-3.5", component.isFavorite && "fill-current")} />
        </Button>

        {/* Color swatches — bottom-left overlay */}
        {component.colors.length > 0 && (
          <div className="absolute bottom-2.5 left-2.5 flex gap-1 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
            {component.colors.slice(0, 4).map((color, index) => (
              <div
                key={index}
                className="h-4 w-4 rounded-full border border-background/50 shadow-sm"
                style={{ backgroundColor: color }}
              />
            ))}
            {component.colors.length > 4 && (
              <div className="flex h-4 w-4 items-center justify-center rounded-full bg-background/80 text-[8px] font-medium text-foreground backdrop-blur-sm">
                +{component.colors.length - 4}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Text — minimal, editorial */}
      <div className="px-1 pb-4 pt-3">
        <p className="mb-1 font-sans text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
          {subCat?.name || component.subCategory}
          {component.finish && <span className="ml-1.5 text-muted-foreground/60">· {component.finish}</span>}
        </p>
        <h3 className="story-link line-clamp-1 font-serif text-sm font-medium leading-snug text-foreground transition-colors duration-300 group-hover:text-primary">
          <span>{component.name}</span>
        </h3>
      </div>
    </div>
  );
}
